import React, { useMemo } from 'react';
import { Button, Tab, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import type { Region } from '@internal/backstage-plugin-bidone-backend';
import { BDirectRow, useBDirectRows } from '../../../hooks/useBDirectRows';
import { useCurrentUserRef } from '../../../hooks/useCurrentUser';
import { BDirectSectionCard } from '../BDirectSectionCard';
import { REGION_COLUMNS } from '../BDirectSectionCard/types';
import { StepEditorDialog } from '../StepEditorDialog';
import type { FilterChip } from '../ReleaseManagement/Filters';
import {
  ChecklistTabs,
  EnvChip,
  Meta,
  SectionDivider,
  TitleRow,
} from './styles';
import { groupBySection, newManualRow, orphanSectionRow } from './helpers';
import { useChecklistDraftHandlers } from './useChecklistDraftHandlers';
import { sameMajorRelease } from '../releaseTag';

interface Props {
  release: string;
  // Filter inputs from `ReleaseChecklistFetch`. `null` / empty Set mean
  // "no filter applied".
  ownerFilter?: string | null;
  stateFilter?: Set<FilterChip>;
}

export const ChecklistDetail = ({
  release,
  ownerFilter,
  stateFilter,
}: Props) => {
  const currentUser = useCurrentUserRef();
  const { env, view, items, isFetching, error } = useBDirectRows(release);

  const groups = useMemo(() => groupBySection(items), [items]);

  const handlers = useChecklistDraftHandlers({
    release,
    items,
    currentUser,
  });
  const {
    drafts,
    editingStep,
    setEditingStep,
    savePending,
    cancelPending,
    deletePending,
    lockPendingSectionId,
    updateDraftSteps,
    updateDraftSectionText,
    handleEditSection,
    handleSaveSection,
    handleCancelSection,
    handleDeleteSection,
    handleChangeState,
  } = handlers;

  const editingDraft =
    editingStep && drafts[editingStep.sectionId]
      ? drafts[editingStep.sectionId]?.steps.find(
          r => r.id === editingStep.stepId,
        )
      : null;

  if (isFetching && !view) return <Progress />;
  if (error)
    return (
      <ResponseErrorPanel
        error={error}
        title="Fetching checklist"
        defaultExpanded
      />
    );
  if (!view) return null;

  // Apply Filters-panel selections at render time. We deliberately don't
  // filter the underlying `groups` / `items` arrays — edit mode, lock
  // handling, and `useEffect` re-entry all depend on the unfiltered shape.
  // A section in active edit always renders so the user doesn't lose
  // their draft when toggling a filter.
  const ownerMatches = (row: BDirectRow) =>
    !ownerFilter || row.owner === ownerFilter;
  // Chip filter semantics:
  //   - No chips selected → everything passes.
  //   - Any chip selected → repo rows pass iff their `state` is in the
  //     selected set; non-repo rows pass iff `non_repo` is in the
  //     selected set. The two row categories are filtered independently
  //     because non-repo rows don't carry a meaningful state.
  const stateMatches = (row: BDirectRow) => {
    if (!stateFilter || stateFilter.size === 0) return true;
    if (!row.repo) return stateFilter.has('non_repo');
    return stateFilter.has(row.state);
  };
  // Non-repo rows whose template declares NO checkbox in the current env
  // (so every region cell would be `—`) aren't actionable for this
  // release — typically the row exists only for the opposite env
  // (UAT-only step on a `*-prod` release, or vice versa). Drop them so
  // the table doesn't carry rows the user can't tick. Repo rows pass
  // through unconditionally — their region cells come from Azure tags,
  // not the template's `completed` schema.
  const envApplicable = (row: BDirectRow) => {
    if (row.repo) return true;
    return Object.values(row.completed).some(
      envs => envs?.[env] !== undefined,
    );
  };

  // For a repo row in `tagged` state, decide whether every applicable
  // region currently reads as "deployed at this release".
  //
  // Applicability is template-driven: a region counts for this row only
  // when the Confluence template declared a `completed[region][env]`
  // cell. Regions outside the template (ASE / NEU for most rows, which
  // are failover or out-of-scope) are skipped — RegionCell renders them
  // as `—` for exactly the same reason, so the divider and the cells
  // agree by construction.
  //
  // For each applicable region the deployment cascade is:
  //   1. Classic Release w/ stages + matching `<env>-<region>` stage:
  //      `succeeded` counts as deployed; anything else falls through to
  //      the Azure-tag check below (so a stuck pipeline can still pass
  //      if the previous same-major release is still live in Azure).
  //   2. `manualTicks` is false → Azure-tag check. Tag matches the
  //      release exactly, or shares the same major prefix (e.g. rc1 vs
  //      rc2 on `2604.0`) → counts as deployed.
  //   3. `manualTicks` is true → fall back to the template-declared
  //      checkbox value.
  //
  // Special case: no build at all → RegionCell renders tag labels /
  // dashes, never a checkbox. Block — the Release team has no signal
  // to confirm deployment against.
  const isRepoRowDeployed = (row: BDirectRow): boolean => {
    if (!row.build) return false;
    const envLower = env.toLowerCase();
    const isReleaseWithStages =
      row.build.kind === 'release' && (row.build.stages?.length ?? 0) > 0;
    for (const region of REGION_COLUMNS) {
      const declared = row.completed[region]?.[env];
      if (declared === undefined) continue; // template-declared not-applicable
      const lower = region.toLowerCase();
      if (isReleaseWithStages) {
        const stage = row.build.stages!.find(s => {
          const tokens = s.name
            .toLowerCase()
            .split(/[^a-z0-9]+/)
            .filter(Boolean);
          return tokens.includes(envLower) && tokens.includes(lower);
        });
        if (stage && stage.status.toLowerCase() === 'succeeded') {
          continue; // this region is deployed via the current release's stage
        }
        // Otherwise — stage missing, notStarted, failed, rejected, etc. —
        // fall through to the Azure-tag check. A previous same-major
        // release still live in Azure is enough to count.
      }
      if (!row.manualTicks) {
        const tag = row.perRegionTag[lower];
        // No Azure resource for this `<repo, env, region>` triple — the
        // app doesn't deploy here. The template may over-declare regions
        // (e.g. ASE listed for all 6 even though newer services skip it),
        // but Azure is the actual source of truth for where the app
        // lives. RegionCell renders this cell as `—`; treat it as
        // not-applicable in readiness for symmetry.
        if (!tag) continue;
        if (sameMajorRelease(tag, release)) continue;
        return false; // tag is from a different major release
      }
      // manualTicks fallback: use the template-declared checkbox value.
      if (!declared) return false;
    }
    return true;
  };

  // Section is "ready" when every actionable signal in the given row
  // list has been resolved. Drives the divider colour — ready sections
  // get a muted grey rule below them so the user's eye skips past
  // finished work.
  //
  // Caller passes the *visible* row list (post owner / state / env
  // filtering), so the divider always agrees with what's on screen.
  //
  // Per-row contract:
  //   - State `no_changes`   → vacuously ready (nothing to deploy).
  //   - State `not_ready`    → blocks — the dev hasn't finished their part.
  //   - State `tagged`       → dev says done; every applicable region must
  //                            now read as deployed at the release tag (via
  //                            `isRepoRowDeployed`). A tag in code with no
  //                            Azure ticks yet is NOT ready — the previous
  //                            logic collapsed `tagged` and `no_changes`
  //                            and let unfinished deploys through.
  //   - Non-repo rows        → every region checkbox the template declared
  //                            for the current env must be ticked.
  const stepsAreReady = (rows: BDirectRow[]) => {
    for (const s of rows) {
      if (s.kind !== 'step') continue;
      if (s.repo) {
        if (s.state === 'no_changes') continue;
        if (s.state === 'not_ready') return false;
        // s.state === 'tagged'
        if (!isRepoRowDeployed(s)) return false;
        continue;
      }
      for (const envs of Object.values(s.completed)) {
        const value = envs?.[env];
        if (value === undefined) continue;
        if (!value) return false;
      }
    }
    return true;
  };

  return (
    <>
      <TitleRow>
        <Typography variant="h4">
          {release}
          <EnvChip>{env}</EnvChip>
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<PrintIcon />}
          href={`/release-checklist/print/${encodeURIComponent(release)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Print
        </Button>
      </TitleRow>
      <ChecklistTabs
        value="bdirect"
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab value="bdirect" label="BDirect" />
      </ChecklistTabs>
      <Meta>
        {view.items.filter(i => i.kind === 'step').length} step
        {view.items.length === 1 ? '' : 's'} across {groups.length} section
        {groups.length === 1 ? '' : 's'}
      </Meta>

      {/* Restrict editing to one section at a time so the user never has
          multiple in-flight drafts to reconcile. Other sections' Edit
          buttons stay disabled until the current draft is saved/cancelled.
          `prevVisibleIndex` tracks the most recent group we actually
          rendered so the divider before the next visible group can read
          *that* group's readiness — without this, a hidden group's
          divider would leak through as a free-floating bar above the
          next visible one. */}
      {(() => {
        let prevVisibleIndex = -1;
        let prevVisibleSteps: BDirectRow[] = [];
        return groups.map((group, gi) => {
        const anyEditing = Object.keys(drafts).length > 0;
        const sectionId = group.section?.id ?? `orphan-${gi}`;
        const isEditingThis = drafts[sectionId] !== undefined;
        const visibleSteps = group.steps.filter(
          s => envApplicable(s) && ownerMatches(s) && stateMatches(s),
        );
        // Truly-empty sections (no steps at all) still render by default
        // — the user needs the section header in view so they can enter
        // Edit mode and add steps. Two cases that override that:
        //   - Sections whose steps got filtered out entirely (owner /
        //     state / env-applicability) are noise the user toggled
        //     themselves; hide.
        //   - When a state chip is active, hide empty sections too —
        //     they have no steps to satisfy "state = X", so they don't
        //     belong in the chip's result set.
        const anyStateChip = !!stateFilter && stateFilter.size > 0;
        const isEmpty = group.steps.length === 0;
        if (!isEditingThis) {
          if (!isEmpty && visibleSteps.length === 0) return null;
          if (isEmpty && anyStateChip) return null;
        }
        const sectionRow = group.section ?? orphanSectionRow(sectionId);
        const draft = drafts[sectionId];
        const isEditing = draft !== undefined;

        // Edit mode hides the same env-only rows that view mode hides
        // (e.g. UAT-only steps on a `*-prod` release). The owner / state
        // chips deliberately don't apply here — those are exploration
        // filters the user toggled, not data-shape rules; hiding rows
        // mid-edit because of a chip would be surprising. The hidden
        // rows are still present in the draft so Save round-trips them
        // unchanged.
        const editableSteps = isEditing
          ? draft.steps.filter(envApplicable)
          : null;

        const card = isEditing ? (
          <BDirectSectionCard
            key={sectionId}
            mode="edit"
            release={release}
            env={env}
            section={sectionRow}
            steps={editableSteps!}
            sectionText={draft.sectionText}
            onChangeState={handleChangeState}
            onUpdateSectionText={next =>
              updateDraftSectionText(sectionId, next)
            }
            onSave={() => handleSaveSection(sectionId)}
            onCancel={() => handleCancelSection(sectionId)}
            onDelete={() => handleDeleteSection(sectionId)}
            savePending={savePending}
            cancelPending={cancelPending}
            deletePending={deletePending}
            onOpenStepEditor={stepId => setEditingStep({ sectionId, stepId })}
            onMoveStep={(stepId, dir) =>
              updateDraftSteps(sectionId, rows => {
                const idx = rows.findIndex(r => r.id === stepId);
                const target = idx + dir;
                if (idx === -1 || target < 0 || target >= rows.length)
                  return rows;
                const next = rows.slice();
                [next[idx], next[target]] = [next[target], next[idx]];
                return next;
              })
            }
            onRemoveStep={stepId =>
              updateDraftSteps(sectionId, rows =>
                rows.filter(r => r.id !== stepId),
              )
            }
            onAddManualStep={() => {
              // Append the row and immediately open the editor on it so
              // the user can supply text + region selection in one flow.
              // The row is removed again if they cancel without
              // typing anything (see StepEditorDialog onCancel below).
              const fresh = newManualRow(env);
              updateDraftSteps(sectionId, rows => [...rows, fresh]);
              setEditingStep({ sectionId, stepId: fresh.id });
            }}
            onToggleRegion={(stepId, region, toggleEnv, value) =>
              updateDraftSteps(sectionId, rows =>
                rows.map(r =>
                  r.id === stepId
                    ? {
                        ...r,
                        completed: {
                          ...r.completed,
                          [region]: {
                            ...(r.completed[region] ?? {}),
                            [toggleEnv]: value,
                          },
                        },
                      }
                    : r,
                ),
              )
            }
          />
        ) : (
          <BDirectSectionCard
            key={sectionId}
            mode="view"
            release={release}
            env={env}
            section={sectionRow}
            steps={visibleSteps}
            lockedBy={sectionRow.lockedBy}
            currentUser={currentUser}
            onChangeState={handleChangeState}
            onEdit={() => handleEditSection(sectionId)}
            editPending={lockPendingSectionId === sectionId}
            editDisabled={anyEditing}
          />
        );

        const showDivider = prevVisibleIndex >= 0;
        // Readiness is computed against the previous group's *visible*
        // steps (not all of its steps), so when a chip filter has the
        // user looking at "everything Tagged" the divider goes grey
        // even though hidden rows in that section may not be ready.
        const dividerReady = showDivider && stepsAreReady(prevVisibleSteps);
        prevVisibleIndex = gi;
        prevVisibleSteps = visibleSteps;

        return (
          <React.Fragment key={sectionId}>
            {showDivider && <SectionDivider ready={dividerReady} />}
            {card}
          </React.Fragment>
        );
        });
      })()}

      <StepEditorDialog
        open={!!editingStep && !!editingDraft}
        initialHtml={editingDraft?.text ?? ''}
        regions={REGION_COLUMNS}
        initialSelectedRegions={
          editingDraft
            ? (Object.keys(editingDraft.completed) as Region[])
            : (REGION_COLUMNS as readonly Region[])
        }
        initialRepo={editingDraft?.repo ?? null}
        title={editingDraft?.text ? 'Edit step' : 'Add step'}
        onCancel={() => {
          // If the user cancels a freshly-added (text-empty) row, drop
          // the row entirely — it was only added as a placeholder for
          // the dialog and never had real content.
          if (editingStep && editingDraft && !editingDraft.text) {
            const { sectionId: sid, stepId: rid } = editingStep;
            updateDraftSteps(sid, rows => rows.filter(r => r.id !== rid));
          }
          setEditingStep(null);
        }}
        onSave={(html, selectedRegions, repo) => {
          if (editingStep && editingDraft) {
            const selected = new Set(selectedRegions);
            updateDraftSteps(editingStep.sectionId, rows =>
              rows.map(r => {
                if (r.id !== editingStep.stepId) return r;
                // Keep existing region entries for selected regions
                // (so cross-env ticks survive an unrelated text edit);
                // drop unselected regions entirely; seed any newly-
                // selected region with an unticked checkbox for the
                // current env so it renders something the user can act on.
                const completed: BDirectRow['completed'] = {};
                for (const region of selected) {
                  completed[region] = r.completed[region] ?? { [env]: false };
                }
                return { ...r, text: html, repo, completed };
              }),
            );
          }
          setEditingStep(null);
        }}
      />
    </>
  );
};
